import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, Optional, Output, Renderer2, Self } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

@Component({
  selector: 'cs-input',
  standalone: true,
  imports: [NgClass],
  templateUrl: './cs-input.component.html',
  styleUrl: './cs-input.component.scss',
})
export class CSInputComponent implements ControlValueAccessor {
  // --------------------------- input Attribute ---------------------------
  static nextId = 0;

  /** 表单的控件名称，作为标签和输入元素的键值对 */
  @Input() id = `cs-input-${CSInputComponent.nextId++}`;

  /** 表单控件的类型 */
  @Input() type: 'text' | 'number' | 'password' = 'text';

  /** 表单控件的初始值 */
  @Input()
  get value(): unknown {
    return this._value;
  }
  set value(newValue: unknown) {
    if (newValue === this._value) return; // 如果新值和当前值相同，则不做任何操作

    const formattedValue = this.format ? this.format(newValue) : newValue;
    this._value = formattedValue;
    this.ngControl ? this._onChange(formattedValue) : this.valueChange.emit(formattedValue);
  }
  @Output() valueChange = new EventEmitter<unknown>();

  /** 表单控件是否禁用 */
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(newValue: unknown) {
    this._disabled = coerceBooleanProperty(newValue);
  }
  /** 表單控件是否唯讀 */
  @Input()
  get readonly(): boolean {
    return this._readonly;
  }
  set readonly(newValue: unknown) {
    this._readonly = coerceBooleanProperty(newValue);
  }
  /** 唯讀並以停用外觀加鎖頭呈現，明示欄位無法修改；值仍保留在表單中。 */
  @Input()
  get locked(): boolean {
    return this._locked;
  }
  set locked(newValue: unknown) {
    this._locked = coerceBooleanProperty(newValue);
  }
  /** 当没有值设定时，出现在表单控件上的文字 */
  @Input() placeholder: unknown = '';

  /** 表单自动填充特性提示 */
  @Input() autocomplete: string | undefined = 'off';

  /** 要使用的虚拟键盘配置类型 */
  @Input() inputMode: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search' = 'text';

  /** 最大值 */
  @Input() max: number | undefined;

  /** value 的最大长度（字符数） */
  @Input() maxLength: number | undefined;

  /** 最小值 */
  @Input() min: number | undefined;

  /** value 的最小长度（字符数） */
  @Input() minLength: number | undefined;

  @Input() hintAbsolute = false;

  /** 聚焦事件 */
  @Output() inputFocus = new EventEmitter<FocusEvent>();

  /** 失焦事件 */
  @Output() inputBlur = new EventEmitter<FocusEvent>();

  /** 按下事件 */
  @Output() inputClick: EventEmitter<Event> = new EventEmitter<Event>();

  /** 按下事件 */
  @Output() inputMouseEnter = new EventEmitter<MouseEvent>();

  /** 按下事件 */
  @Output() inputMouseLeave = new EventEmitter<MouseEvent>();

  // --------------------------- field Attribute ---------------------------
  /** 表单的控件标签 */
  @Input() width = '100%';

  @Input() name = '';

  // 把 width 綁定到 host 的 style
  @HostBinding('style.width') get hostWidth() {
    return this.width;
  }

  /** 表单的控件标签 */
  @Input() label: string | undefined;

  /** 放置在表单字段前面的前缀的 icon 名称 */
  @Input() prefixIconName: string | undefined;

  /** 放置在表单字段前面的前缀的 image 路徑 */
  @Input() prefixImgUrl: string | undefined;

  @Input() prefixText?: string;

  /** 放置在表单字段末尾的后缀的 icon 名称 */
  @Input() suffixIconName: string | undefined;

  /** 放置在表单字段末尾的后缀的文字 (会在后缀 icon 之前) */
  @Input() suffixText: string | undefined;

  /** 表单控件是否错误 */
  @Input() hasError: boolean | null | undefined;

  /** 表单控件错误提示文字 */
  @Input() errorHint: string | undefined;

  @Input() marginBottom: string | undefined = '16px';

  /**格式化函数 */
  @Input() format: ((value: any) => any) | undefined;

  /** 放置在表单字段末尾的后缀的 click 事件 */
  @Output() suffixIconClick = new EventEmitter<Event>();

  // --------------------------- reactive forms settings ---------------------------
  constructor(
    private renderer: Renderer2,
    @Optional() @Self() private ngControl: NgControl,
  ) {
    // Replace the provider from above with this.
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
  }

  private _onChange: (value: unknown) => void = () => {};
  private _onTouched: () => void = () => {};

  protected _value: unknown = '';
  protected _focused = false;
  protected _touched = false;
  protected _disabled = false;
  protected _readonly = false;
  protected _locked = false;

  writeValue(value: unknown): void {
    this._value = value ?? '';
  }

  registerOnChange(onChange: (value: unknown) => void): void {
    this._onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this._onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  // --------------------------- template functions ---------------------------
  protected onInput(event: Event): void {
    const element = event.target as HTMLInputElement;
    const rawValue = element.value;
    const start = element.selectionStart ?? rawValue.length;
    this.value = rawValue;

    // 處理 input.value 與紀錄值不同步問題
    if (this.format && rawValue !== this.value) {
      const formattedValue = String(this.value ?? '');
      this.renderer.setProperty(element, 'value', formattedValue);

      // 還原游標：依格式化前後長度差調整，避免自動插字元時游標倒退。
      const delta = formattedValue.length - rawValue.length;
      const position = Math.max(0, Math.min(formattedValue.length, start + delta));
      element.setSelectionRange(position, position);
    }
  }

  protected onFocusIn(event: FocusEvent): void {
    if (this._focused) return;

    this._focused = true;
    this.inputFocus.emit(event);
  }

  protected onFocusOut(event: FocusEvent): void {
    if (!this._focused) return;

    this._touched = true;
    this._focused = false;
    this._onTouched();
    this.inputBlur.emit(event);
  }

  protected onSuffixIconClick(event: Event): void {
    if (this._disabled) return;

    if (!this._touched) {
      this._onTouched();
      this._touched = true;
    }
    this.suffixIconClick.emit(event);
  }
}
